import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import axios from 'axios';
import { baseURL } from '../base_url';
import { PuffLoader } from 'react-spinners';
import { Pagination, Button } from '@mui/material';
import * as XLSX from "xlsx";
import Select from "react-select";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import { MDBDataTable } from "mdbreact";
import './Ridesiq.css';

const RidesiqDevices = () => {
  const [loading, setLoading] = useState(false);
  // all devices for the selected location, loaded once; search + paging happen in the browser
  const [devicesResponse, setDevicesResponse] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [locationItems, setLocationItems] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState({ label: "All", value: "0" });
  const [openEdit, setOpenEdit] = useState(false);
  const [searchIMEI, setSearchIMEI] = useState("");
  const [groupOptions, setGroupOptions] = useState([]);

  const [editData, setEditData] = useState({
    IMEI: "",
    VIN: "",
    LOCATION: "",
    GROUPID:"",
    FLEET_LOCATION: ""
  });

  const loggedInUser = sessionStorage.getItem("tm_token");
  const foundUser = JSON.parse(loggedInUser);

  const handleLocationChange = (selectedOption) => {
    setSelectedLocation(selectedOption);
  };

  // Loads every device for the selected location in one call (fetch_all=true).
  // Called on first load, on Submit, and after an update — never on search or page change.
  const fetchDevices = async (resetPage = true) => {
    setLoading(true);
    try {
      const group = selectedLocation.label;

      const res = await axios.get(
        `${baseURL}/get_ridesiq_devices_for_portal?page=0&size=${pageSize}&group=${encodeURIComponent(group)}&search=&fetch_all=true`
      );

      setDevicesResponse(res.data.data || []);
      if (resetPage) setPage(1);
    } catch (error) {
      Swal.fire("Error", "Failed to fetch devices", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups()
    axios.get(`${baseURL}/get_location_setup?status=active&admin_id=${foundUser['tm_id']}`)
      .then((res) => {
        const allOption = { label: "All", value: "0" };
        const menuList2 = res.data.data.map((val1) => ({
          label: val1.location_name,
          value: val1._id
        }));
        setLocationItems([allOption, ...menuList2]);
      });

    fetchDevices();
  }, []);

  const fetchGroups = async () => {
      try {
        const res = await axios.get(`${baseURL}/get_ridesiq_groups`);
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setGroupOptions(list.map((g) => ({ value: g.value, label: g.label })));
      } catch (error) {
        Swal.fire("Error", "Failed to load RidesIQ groups", "error");
      }
    };


  // ---- client-side search over the loaded devices ----
  const filteredDevices = useMemo(() => {
    const term = searchIMEI.trim().toLowerCase();
    if (!term) return devicesResponse;
    return devicesResponse.filter((item) =>
      [item.IMEI, item.VIN, item.LOCATION, item.FLEET_LOCATION]
        .some((val) => String(val || "").toLowerCase().includes(term))
    );
  }, [devicesResponse, searchIMEI]);

  // ---- client-side pagination ----
  const totalCount = filteredDevices.length;
  const pagedDevices = useMemo(
    () => filteredDevices.slice((page - 1) * pageSize, page * pageSize),
    [filteredDevices, page, pageSize]
  );

   const selectedGroupOption = useMemo(() => {
      if (editData.GROUPID === undefined || editData.GROUPID === null || editData.GROUPID === "") return null;
      return (
        groupOptions.find((opt) => String(opt.value) === String(editData.GROUPID)) ||
        { value: editData.GROUPID, label: editData.LOCATION || String(editData.GROUPID) }
      );
    }, [groupOptions, editData.GROUPID, editData.LOCATION]);

  const handleExportAllDevices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseURL}/get_ridesiq_devices_for_portal?page=0&size=50&group=All&search=&fetch_all=true`);
      const data = res.data.data || [];

      const formattedData = data.map(item => ({
        "RIDESIQ LOCATION": item.LOCATION || "",
        "TELEMATICS LOCATION": item.FLEET_LOCATION || "",
        IMEI: item.IMEI || "",
        VIN: item.VIN || ""
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      worksheet['!cols'] = [{ wch: 15 }, { wch: 18 }, { wch: 20 }, { wch: 20 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Devices');
      XLSX.writeFile(workbook, 'ridesiq_devices.xlsx');
    } catch (error) {
      Swal.fire("Error", "Failed to export devices", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (item) => {
    setEditData({
      IMEI: item.IMEI,
      VIN: item.VIN,
      LOCATION: item.LOCATION || "",
      GROUPID: item.group_id,
      FLEET_LOCATION: item.FLEET_LOCATION || ""
    });
    setOpenEdit(true);
  };

    const handleUpdateDevice = async () => {
        try {
            setLoading(true);
            const payload = {
                admin_id: foundUser['tm_id'],
                imei: editData.IMEI,
                vin: editData.VIN,
                location: editData.LOCATION,
                group_id: editData.GROUPID
            };

            const res = await axios.post(`${baseURL}/update_ridesiq_device_data`, payload);
            if (res.data?.success) {
                setOpenEdit(false);
                fetchDevices(false); // refresh but stay on the current page / search
                Swal.fire(
                    "Success",
                    res.data.message || "Device updated successfully",
                    "success"
                );
            } else {
                // 🔥 parse upstream error if present
                let errorMsg = res.data?.message || "Update failed";

                if (res.data?.error) {
                    try {
                    const parsed = JSON.parse(res.data.error);
                    errorMsg = parsed.message || errorMsg;
                    } catch (e) {
                    errorMsg = res.data.error;
                    }
                }

                Swal.fire("Error", errorMsg, "error");
            }
            // setOpenEdit(false);
            // fetchDevices(page);
        } catch (err) {
            let errorMsg = "Failed to update device";

            if (err.response?.data?.error) {
            try {
                const parsed = JSON.parse(err.response.data.error);
                errorMsg = parsed.message || errorMsg;
            } catch {
                errorMsg = err.response.data.error;
            }
            } else if (err.response?.data?.message) {
            errorMsg = err.response.data.message;
            }

            Swal.fire("Error", errorMsg, "error");
        } finally {
            setLoading(false);
        }
    };

  const tableData = {
    columns: [
      { label: "IMEI", field: "IMEI", sort: "asc" },
      { label: "VIN", field: "VIN", sort: "asc" },
      { label: "RidesIQ Location", field: "LOCATION", sort: "asc" },
      { label: "Telematics Location", field: "FLEET_LOCATION", sort: "asc" },
      { label: "Action", field: "action" }
    ],
    rows: pagedDevices.map((item) => ({
      ...item,
      FLEET_LOCATION: item.FLEET_LOCATION || "-",
      action: (
        <Button size="small" onClick={() => handleEditClick(item)}>
          Edit
        </Button>
      )
    }))
  };

  return (
    <div className='body_class'>
      {loading && (
        <div className="loader-overlay">
          <PuffLoader color="#00308F" size={200} />
        </div>
      )}

      <div className='container-fluid'>
        <h4>RidesIQ Devices</h4>

        <div className='row p-4 align-items-center'>
          <div className="col-auto mt-1">
            <label className="form-label">Location:</label>
          </div>
          <div className='col-2'>
            <Select
              placeholder="Select Location"
              options={locationItems}
              onChange={handleLocationChange}
              value={selectedLocation}
            />
          </div>
          <div className='col-1'>
            <Button variant="contained" onClick={() => fetchDevices()}>Submit</Button>
          </div>
        </div>

        <div className='row'>
            <div className='col-6'>
                <button className='btn btn-success' onClick={handleExportAllDevices}>
                    Export All to Excel
                </button>
            </div>
            <div className='col-2'>
                <input
                type="text"
                className="form-control"
                placeholder="Search IMEI / VIN / Location"
                value={searchIMEI}
                onChange={(e) => {
                    setSearchIMEI(e.target.value);
                    setPage(1);
                }}
                />
            </div>
        </div>
        <br></br>
        <div className='col-8'>
            <MDBDataTable
                striped
                bordered
                hover
                paging={false}
                searching={false}
                data={tableData}
                noBottomColumns={true}
            />
            <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={(e, value) => {
                    setPage(value);
                }}
                color="primary"
                className="mt-3"
            />
        </div>
      

        <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
          <DialogTitle>Edit Device</DialogTitle>
          <DialogContent sx={{ minWidth: 400 }}>
            <TextField margin="dense" label="IMEI" fullWidth value={editData.IMEI} disabled />
            <TextField
              margin="dense"
              label="VIN"
              fullWidth
              value={editData.VIN}
              onChange={(e) => setEditData({ ...editData, VIN: e.target.value })}
            />
               <div style={{ marginTop: 8, marginBottom: 4 }}>
                  <label className="form-label" style={{ fontSize: 13 }}>Location (RidesIQ group)</label>
                  <Select
                    placeholder="Select group"
                    options={groupOptions}
                    value={selectedGroupOption}
                    onChange={(opt) =>
                      setEditData({ ...editData, GROUPID: opt.value, LOCATION: opt.label })
                    }
                    isSearchable
                    // render the menu above the MUI Dialog so it isn't clipped
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1400 }) }}
                  />
                </div>
            <TextField
              margin="dense"
              label="Fleet Location"
              fullWidth
              value={editData.FLEET_LOCATION || "-"}
              disabled
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdateDevice} disabled={loading}>Update</Button>
          </DialogActions>
        </Dialog>

      </div>
    </div>
  );
};

export default RidesiqDevices;
